export type SupportedLanguage =
  | 'en'
  | 'tl'
  | 'ceb'
  | 'ko'
  | 'zh-CN'
  | 'zh-TW'
  | 'es';

export const LANGUAGE_META: Record<
  SupportedLanguage,
  {
    code: SupportedLanguage;
    label: string;
    nativeLabel: string;
    flag: string;
    ttsLang: string;
  }
> = {
  en: {
    code: 'en',
    label: 'English',
    nativeLabel: 'English (KJV / WEB)',
    flag: 'US',
    ttsLang: 'en-US',
  },
  tl: {
    code: 'tl',
    label: 'Filipino / Tagalog',
    nativeLabel: 'Filipino / Tagalog (AB)',
    flag: 'PH',
    ttsLang: 'fil-PH',
  },
  ceb: {
    code: 'ceb',
    label: 'Cebuano / Bisaya',
    nativeLabel: 'Cebuano / Bisaya (BUGNA)',
    flag: 'PH',
    ttsLang: 'ceb-PH',
  },
  ko: {
    code: 'ko',
    label: 'Korean',
    nativeLabel: '한국어 (개역한글)',
    flag: 'KR',
    ttsLang: 'ko-KR',
  },
  'zh-CN': {
    code: 'zh-CN',
    label: 'Simplified Chinese',
    nativeLabel: '简体中文 (和合本)',
    flag: 'CN',
    ttsLang: 'zh-CN',
  },
  'zh-TW': {
    code: 'zh-TW',
    label: 'Traditional Chinese',
    nativeLabel: '繁體中文 (和合本)',
    flag: 'TW',
    ttsLang: 'zh-TW',
  },
  es: {
    code: 'es',
    label: 'Spanish',
    nativeLabel: 'Español (RVR1909)',
    flag: 'ES',
    ttsLang: 'es-ES',
  },
};

export interface UITranslationStrings {
  tagline: string;
  navHome: string;
  navPlay: string;
  navMultiplayer: string;
  navLive: string;
  navRankings: string;
  navProfile: string;
  navAdmin: string;
  heroHeadline: string;
  heroSubtitle: string;
  ctaStartQuiz: string;
  ctaHostLive: string;
  ctaMultiplayer: string;
  dailyChallenge: string;
  dailySubtitle: string;
  playDailyNow: string;
  specialModesTitle: string;
  categoriesTitle: string;
  soloConfigTitle: string;
  soloConfigSubtitle: string;
  selectGameMode: string;
  modePractice: string;
  modePracticeDesc: string;
  modeChallenge: string;
  modeChallengeDesc: string;
  modeRanked: string;
  modeRankedDesc: string;
  selectCategory: string;
  selectDifficulty: string;
  selectFormat: string;
  questionCount: string;
  enableTimer: string;
  enableNarration: string;
  narrateInstructionsLabel: string;
  narrateChoicesLabel: string;
  freshPoolLabel: string;
  freshPoolDesc: string;
  generateAndPlay: string;
  generatingAiQuestions: string;
  scriptureReference: string;
  explanation: string;
  nextQuestion: string;
  finishQuiz: string;
  scoreLabel: string;
  streakLabel: string;
  accuracyLabel: string;
  multiplayerLobby: string;
  quickMatch: string;
  createPrivateRoom: string;
  joinByPin: string;
  liveEventCenter: string;
  createLiveEvent: string;
  openProjectorView: string;
  joinLiveEvent: string;
  timerSwitchLabel: string;
  globalLeaderboards: string;
  signInWithGoogle: string;
  signOut: string;
  saveProfile: string;
  questionPrefix: string;
  optionsPrefix: string;
  correctFeedback: string;
  incorrectFeedback: string;
  timeoutFeedback: string;
  playAgain: string;
  backToHome: string;
  quizCompletedTitle: string;
  xpGainedLabel: string;
  newBadgesUnlocked: string;
  correctAnswersCount: string;
  bestStreakLabel: string;
  selectAnswerPrompt: string;
  trueFalsePrompt: string;
  fillBlankPrompt: string;
  shortAnswerPrompt: string;
  matchingPrompt: string;
  openEndedPrompt: string;
  trueLabel: string;
  falseLabel: string;
  typeAnswerPlaceholder: string;
  submitAnswerBtn: string;
  wordBankMode: string;
  typeWordMode: string;
  matchingColumnA: string;
  matchingColumnB: string;
  submitMatchesBtn: string;
  clearMatchesBtn: string;
  pointsEarned: string;
  speedBonus: string;
  streakBonus: string;
  listenQuestionBtn: string;
  tooltipScore: string;
  tooltipAccuracy: string;
  tooltipBestStreak: string;
  tooltipXpGained: string;
  tooltipLevelProgress: string;
  tooltipLifetimeAccuracy: string;
  ariaHowScoreCalculated: string;
  ariaHowAccuracyCalculated: string;
  ariaHowBestStreakCalculated: string;
  ariaHowXpCalculated: string;
  ariaHowLevelCalculated: string;
  ariaHowLifetimeAccuracyCalculated: string;
}

export const UI_TRANSLATIONS: Record<SupportedLanguage, UITranslationStrings> = {
  en: {
    tagline: 'Bible Knowledge. Global Competition.',
    navHome: 'Home',
    navPlay: 'Play',
    navMultiplayer: 'Multiplayer',
    navLive: 'LIVE',
    navRankings: 'Rankings',
    navProfile: 'Profile',
    navAdmin: 'Admin',
    heroHeadline: 'Master Scripture Through Global Play & Live Competition',
    heroSubtitle:
      'Unlimited Verified Bible questions, real-time global matchmaking, church tournament arenas, and auditorium-grade LIVE events.',
    ctaStartQuiz: 'Play Solo Quiz',
    ctaHostLive: 'Host LIVE Event',
    ctaMultiplayer: 'Global Matchmaking',
    dailyChallenge: 'Daily Bible Challenge',
    dailySubtitle: 'Fresh Challenges Every Game · Earn 2x XP & Streak Shield',
    playDailyNow: 'Start Daily Challenge',
    specialModesTitle: 'Specialized Scripture Experiences',
    categoriesTitle: 'Canonical & Thematic Categories',
    soloConfigTitle: 'Configure Your Bible Quiz Session',
    soloConfigSubtitle:
      'Choose your game mode, quiz type, Bible category, difficulty, and multilingual voice narration.',
    selectGameMode: 'Quiz Purpose / Game Mode',
    modePractice: 'Practice Mode',
    modePracticeDesc: 'Relaxed study · Optional timer · No ranking impact',
    modeChallenge: 'Challenge Mode',
    modeChallengeDesc: 'Scored session · XP rewards · Personal records',
    modeRanked: 'Ranked Mode',
    modeRankedDesc: 'Strict rules · Standardized timer · Leaderboard impact',
    selectCategory: 'Bible Category',
    selectDifficulty: 'Difficulty Level',
    selectFormat: 'Quiz Type',
    questionCount: 'Number of Questions',
    enableTimer: 'Countdown Timer',
    enableNarration: 'Automatic Narration (TTS)',
    narrateInstructionsLabel: 'Narrate Quiz Instructions',
    narrateChoicesLabel: 'Narrate Answer Choices (Accessibility)',
    freshPoolLabel: 'Expand Question Pool',
    freshPoolDesc: 'Prioritize unseen canonical questions',
    generateAndPlay: 'Start Bible Quiz',
    generatingAiQuestions: 'Preparing fresh canonical Scripture questions...',
    scriptureReference: 'Scripture Reference',
    explanation: 'Biblical Context & Explanation',
    nextQuestion: 'Next Question',
    finishQuiz: 'View Final Results',
    scoreLabel: 'Score',
    streakLabel: 'Streak',
    accuracyLabel: 'Accuracy',
    multiplayerLobby: 'Real-Time Global Matchmaking',
    quickMatch: 'Quick Global Match',
    createPrivateRoom: 'Create Private Room',
    joinByPin: 'Join Room by PIN',
    liveEventCenter: 'Auditorium-Grade LIVE Events',
    createLiveEvent: 'Create LIVE Event',
    openProjectorView: 'Launch 16:9 Projector Display',
    joinLiveEvent: 'Participant Mobile Join',
    timerSwitchLabel: 'Authoritative Server Timer',
    globalLeaderboards: 'Global Hall of Champions',
    signInWithGoogle: 'Sign in with Google',
    signOut: 'Sign Out',
    saveProfile: 'Save Profile & Preferences',
    questionPrefix: 'Question',
    optionsPrefix: 'Options',
    correctFeedback: 'Correct!',
    incorrectFeedback: 'Incorrect.',
    timeoutFeedback: 'Time Expired!',
    playAgain: 'Play Again',
    backToHome: 'Return to Home',
    quizCompletedTitle: 'Quiz Session Completed!',
    xpGainedLabel: 'XP Gained',
    newBadgesUnlocked: 'New Badges Unlocked',
    correctAnswersCount: 'Correct Answers',
    bestStreakLabel: 'Best Streak',
    selectAnswerPrompt: 'Select the correct biblical answer below',
    trueFalsePrompt: 'Determine whether this biblical statement is True or False',
    fillBlankPrompt: 'Supply the missing biblical word to complete the passage',
    shortAnswerPrompt: 'Type the correct biblical person, place, book, or answer',
    matchingPrompt: 'Tap an item on the left, then tap its matching biblical pair on the right',
    openEndedPrompt: 'Write your biblical reflection below and compare with Scripture context',
    trueLabel: 'TRUE',
    falseLabel: 'FALSE',
    typeAnswerPlaceholder: 'Type your biblical answer...',
    submitAnswerBtn: 'Submit Answer',
    wordBankMode: 'Word Selection',
    typeWordMode: 'Type Answer',
    matchingColumnA: 'Biblical Item',
    matchingColumnB: 'Matching Connection',
    submitMatchesBtn: 'Submit All Matches',
    clearMatchesBtn: 'Reset Pairs',
    pointsEarned: 'Points Earned',
    speedBonus: 'Speed Bonus',
    streakBonus: 'Streak Bonus',
    listenQuestionBtn: 'Narrate Question',
    tooltipScore:
      'Your points for this game only. Each correct answer earns {correctBase} points. Answer faster for up to {speedBonusMax} bonus points. Getting answers right in a row adds {streakStep} bonus points per answer in the streak, up to {streakMax}. Wrong, skipped or timed-out answers earn {wrongPoints}, and nothing is ever taken away.',
    tooltipAccuracy:
      'How many questions you got right out of all the questions in this game. Skipped and timed-out questions count as wrong. Example: 2 of 5 correct = 40%.',
    tooltipBestStreak:
      'Your longest run of correct answers in a row during this game. A wrong, skipped or timed-out answer resets the streak.',
    tooltipXpGained:
      'XP is your long-term progress. You earn about 1 XP for every {scoreDivisor} points of Score, at least {minPerCorrect} XP per correct answer, plus {perfectBonus} bonus XP for a perfect game. XP raises your level and your rank on the leaderboard.',
    tooltipLevelProgress:
      'Level goes up as you earn XP. Each new level needs more XP than the last.',
    tooltipLifetimeAccuracy:
      'Shown after you\'ve played {minQuestions} questions.',
    ariaHowScoreCalculated: 'How Score is calculated',
    ariaHowAccuracyCalculated: 'How Accuracy is calculated',
    ariaHowBestStreakCalculated: 'How Best Streak is calculated',
    ariaHowXpCalculated: 'How XP is calculated',
    ariaHowLevelCalculated: 'How Level is calculated',
    ariaHowLifetimeAccuracyCalculated: 'How Lifetime Accuracy is calculated',
  },
  tl: {
    tagline: 'Kaalaman sa Bibliya. Pandaigdigang Tagisan.',
    navHome: 'Tahanan',
    navPlay: 'Maglaro',
    navMultiplayer: 'Multiplayer',
    navLive: 'LIVE',
    navRankings: 'Ranggo',
    navProfile: 'Profile',
    navAdmin: 'Admin',
    heroHeadline: 'Unawain ang Banal na Kasulatan sa Pandaigdigang Tagisan',
    heroSubtitle:
      'Walang-katapusang Tanong sa Bibliya, Bagong Hamon sa Bawat Laro, Real-Time Pandaigdigang Laban, Paligsahan sa Simbahan, at LIVE Events.',
    ctaStartQuiz: 'Maglaro ng Solo Quiz',
    ctaHostLive: 'Mag-host ng LIVE Event',
    ctaMultiplayer: 'Pandaigdigang Laban',
    dailyChallenge: 'Pang-araw-araw na Hamon sa Bibliya',
    dailySubtitle: 'Bagong hanay ng mga tanong · Kumita ng 2x XP',
    playDailyNow: 'Simulan ang Hamon Ngayon',
    specialModesTitle: 'Mga Espesyal na Laro sa Kasulatan',
    categoriesTitle: 'Mga Kategorya sa Lumang at Bagong Tipan',
    soloConfigTitle: 'I-set up ang Iyong Bible Quiz',
    soloConfigSubtitle:
      'Piliin ang mode ng laro, uri ng pagsusulit, kategorya sa Bibliya, antas ng hirap, at pagsasalaysay ng boses.',
    selectGameMode: 'Layunin ng Pagsusulit / Mode',
    modePractice: 'Practice Mode',
    modePracticeDesc: 'Kalmado · Opsyonal na orasan · Walang epekto sa ranggo',
    modeChallenge: 'Challenge Mode',
    modeChallengeDesc: 'May puntos · Gantimpalang XP · Personal na rekord',
    modeRanked: 'Ranked Mode',
    modeRankedDesc: 'Mahigpit na tuntunin · Kasama sa Pandaigdigang Leaderboard',
    selectCategory: 'Pumili ng Kategorya',
    selectDifficulty: 'Antas ng Hirap',
    selectFormat: 'Uri ng Pagsusulit (Quiz Type)',
    questionCount: 'Bilang ng mga Tanong',
    enableTimer: 'Orasan (Countdown)',
    enableNarration: 'Awtomatikong Boses (TTS)',
    narrateInstructionsLabel: 'Basahin ang Panuto',
    narrateChoicesLabel: 'Basahin ang mga Pagpipilian (Accessibility)',
    freshPoolLabel: 'Lawakan ang mga Tanong',
    freshPoolDesc: 'Unahin ang mga bagong tanong',
    generateAndPlay: 'Simulan ang Pagsusulit',
    generatingAiQuestions: 'Inihahanda ang mga tanong mula sa Bibliya...',
    scriptureReference: 'Talata sa Bibliya',
    explanation: 'Paliwanag at Konteksto',
    nextQuestion: 'Susunod na Tanong',
    finishQuiz: 'Tingnan ang Resulta',
    scoreLabel: 'Puntos',
    streakLabel: 'Sunod-sunod',
    accuracyLabel: 'Katumpakan',
    multiplayerLobby: 'Pandaigdigang Multiplayer Arena',
    quickMatch: 'Mabilisang Laban',
    createPrivateRoom: 'Gumawa ng Pribadong Kwarto',
    joinByPin: 'Sumali Gamit ang PIN',
    liveEventCenter: 'LIVE Event Mode para sa Simbahan at Paaralan',
    createLiveEvent: 'Gumawa ng LIVE Event',
    openProjectorView: 'Buksan ang 16:9 Projector Screen',
    joinLiveEvent: 'Sumali Bilang Kalahok',
    timerSwitchLabel: 'Server Timer (ON/OFF)',
    globalLeaderboards: 'Pandaigdigang Talaan ng mga Kampeon',
    signInWithGoogle: 'Mag-sign in gamit ang Google',
    signOut: 'Mag-log out',
    saveProfile: 'I-save ang Profile at Settings',
    questionPrefix: 'Tanong',
    optionsPrefix: 'Mga Pagpipilian',
    correctFeedback: 'Tama!',
    incorrectFeedback: 'Mali.',
    timeoutFeedback: 'Ubos na ang Oras!',
    playAgain: 'Maglaro Muli',
    backToHome: 'Bumalik sa Tahanan',
    quizCompletedTitle: 'Natapos na ang Pagsusulit!',
    xpGainedLabel: 'XP na Nakuha',
    newBadgesUnlocked: 'Mga Bagong Badge na Nakuha',
    correctAnswersCount: 'Tamang Sagot',
    bestStreakLabel: 'Pinakamahusay na Sunod-sunod',
    selectAnswerPrompt: 'Piliin ang tamang sagot sa Bibliya sa ibaba',
    trueFalsePrompt: 'Tukuyin kung Tama (True) o Mali (False) ang pahayag sa Bibliya',
    fillBlankPrompt: 'Punan ang nawawalang salita upang mabuo ang talata',
    shortAnswerPrompt: 'I-type ang tamang tao, lugar, aklat, o sagot sa Bibliya',
    matchingPrompt: 'Pindutin ang nasa kaliwa at itugma sa tamang kapares sa kanan',
    openEndedPrompt: 'Isulat ang iyong pagninilay sa Bibliya sa ibaba',
    trueLabel: 'TAMA (TRUE)',
    falseLabel: 'MALI (FALSE)',
    typeAnswerPlaceholder: 'I-type ang iyong sagot...',
    submitAnswerBtn: 'Ipasa ang Sagot',
    wordBankMode: 'Pumili ng Salita',
    typeWordMode: 'I-type ang Sagot',
    matchingColumnA: 'Aytem sa Bibliya',
    matchingColumnB: 'Katugmang Kaugnayan',
    submitMatchesBtn: 'Ipasa ang mga Tugma',
    clearMatchesBtn: 'Ulitin',
    pointsEarned: 'Puntos na Nakuha',
    speedBonus: 'Bilis na Bonus',
    streakBonus: 'Streak na Bonus',
    listenQuestionBtn: 'Pakinggan',
    tooltipScore:
      'Ang iyong puntos sa larong ito lamang. Bawat tamang sagot ay may {correctBase} puntos. Mas mabilis na pagsagot ay may hanggang {speedBonusMax} bonus na puntos. Ang sunod-sunod na tamang sagot ay nagdaragdag ng {streakStep} bonus na puntos kada streak, hanggang {streakMax}. Ang mali, hinto o naubos na oras ay {wrongPoints} puntos, at walang binabawas.',
    tooltipAccuracy:
      'Ilang tanong ang nakuha mong tama sa lahat ng tanong sa larong ito. Ang nilaktawan at naubusan ng oras ay itinuturing na mali. Halimbawa: 2 sa 5 tama = 40%.',
    tooltipBestStreak:
      'Ang iyong pinakamahabang sunod-sunod na tamang sagot sa larong ito. Ang maling sagot, paglaktaw o pagkaubos ng oras ay nagre-reset ng streak.',
    tooltipXpGained:
      'Ang XP ay ang iyong pangmatagalang pag-unlad. Makakakuha ka ng mga 1 XP sa bawat {scoreDivisor} puntos ng Score, hindi bababa sa {minPerCorrect} XP kada tamang sagot, plus {perfectBonus} bonus na XP para sa perpektong laro. Ang XP ay nagpapatas ng iyong antas at ranggo sa leaderboard.',
    tooltipLevelProgress:
      'Tumaas ang antas kapag nakakakuha ka ng XP. Bawat bagong antas ay nangangailangan ng mas maraming XP kaysa sa huli.',
    tooltipLifetimeAccuracy:
      'Ipinapakita pagkatapos mong makapaglaro ng {minQuestions} tanong.',
    ariaHowScoreCalculated: 'Paano kinakalkula ang Score',
    ariaHowAccuracyCalculated: 'Paano kinakalkula ang Accuracy',
    ariaHowBestStreakCalculated: 'Paano kinakalkula ang Best Streak',
    ariaHowXpCalculated: 'Paano kinakalkula ang XP',
    ariaHowLevelCalculated: 'Paano kinakalkula ang Level',
    ariaHowLifetimeAccuracyCalculated: 'Paano kinakalkula ang Lifetime Accuracy',
  },
  ceb: {
    tagline: 'Kahibalo sa Bibliya. Tibuok Kalibutan nga Panagsangka.',
    navHome: 'Panimalay',
    navPlay: 'Dula',
    navMultiplayer: 'Multiplayer',
    navLive: 'LIVE',
    navRankings: 'Ranggo',
    navProfile: 'Profile',
    navAdmin: 'Admin',
    heroHeadline: 'Kab-ota ang Balaang Kasulatan Pinaagi sa Tibuok Kalibutan nga Dula',
    heroSubtitle:
      'Walay Kinutuban nga mga Pangutana sa Bibliya, Bag-ong Hagit Matag Dula, Real-Time nga Panagsangka, Torneyo sa Simbahan, ug LIVE Events.',
    ctaStartQuiz: 'Dula sa Solo Quiz',
    ctaHostLive: 'Mag-host og LIVE Event',
    ctaMultiplayer: 'Global nga Panagsangka',
    dailyChallenge: 'Adlaw-adlaw nga Hagit sa Bibliya',
    dailySubtitle: 'Bag-ong mga pangutana matag dula · Pagdawat og 2x XP',
    playDailyNow: 'Sugdi ang Hagit Karon',
    specialModesTitle: 'Espesyal nga mga Dula sa Kasulatan',
    categoriesTitle: 'Mga Kategorya sa Daan ug Bag-ong Tugon',
    soloConfigTitle: 'I-andam ang Imong Bible Quiz',
    soloConfigSubtitle:
      'Pilia ang mode sa dula, matang sa quiz, kategorya sa Bibliya, kalisod, ug tingog nga nagbasa.',
    selectGameMode: 'Katuyoan sa Quiz / Mode sa Dula',
    modePractice: 'Practice Mode',
    modePracticeDesc: 'Relaks nga pagtuon · Opsyonal nga orasan · Walay epekto sa ranggo',
    modeChallenge: 'Challenge Mode',
    modeChallengeDesc: 'May iskor · Ganti nga XP · Personal nga rekord',
    modeRanked: 'Ranked Mode',
    modeRankedDesc: 'Estrikto nga lagda · Epekto sa Global Leaderboard',
    selectCategory: 'Pilia ang Kategorya',
    selectDifficulty: 'Lebel sa Kalisod',
    selectFormat: 'Matang sa Quiz (Quiz Type)',
    questionCount: 'Gidaghanon sa Pangutana',
    enableTimer: 'Orasan (Countdown)',
    enableNarration: 'Awtomatikong Tingog (TTS)',
    narrateInstructionsLabel: 'Basaha ang Instruksyon',
    narrateChoicesLabel: 'Basaha ang mga Kapilian (Accessibility)',
    freshPoolLabel: 'Dugangi ang mga Pangutana',
    freshPoolDesc: 'Unaha ang bag-ong mga pangutana',
    generateAndPlay: 'Sugdi ang Quiz',
    generatingAiQuestions: 'Nag-andam sa mga pangutana gikan sa Bibliya...',
    scriptureReference: 'Bersikulo sa Bibliya',
    explanation: 'Pagpasabot sa Kasulatan',
    nextQuestion: 'Sunod nga Pangutana',
    finishQuiz: 'Tan-awa ang Resulta',
    scoreLabel: 'Iskor',
    streakLabel: 'Sunod-sunod',
    accuracyLabel: 'Katukma',
    multiplayerLobby: 'Global Real-Time Multiplayer Arena',
    quickMatch: 'Dali nga Panagsangka',
    createPrivateRoom: 'Paghimo og Pribadong Kwarto',
    joinByPin: 'Apil Gamit ang PIN',
    liveEventCenter: 'LIVE Event Mode alang sa Simbahan ug Kombensyon',
    createLiveEvent: 'Paghimo og LIVE Event',
    openProjectorView: 'Ablihi ang 16:9 Projector Screen',
    joinLiveEvent: 'Apil isip Partisipante',
    timerSwitchLabel: 'Server Timer (ON/OFF)',
    globalLeaderboards: 'Tibuok Kalibutan nga Talaan sa mga Kampeon',
    signInWithGoogle: 'Sign in gamit ang Google',
    signOut: 'Sign Out',
    saveProfile: 'I-save ang Profile',
    questionPrefix: 'Pangutana',
    optionsPrefix: 'Mga Kapilian',
    correctFeedback: 'Sakto!',
    incorrectFeedback: 'Sayop.',
    timeoutFeedback: 'Nahurot na ang Oras!',
    playAgain: 'Dula Pag-usab',
    backToHome: 'Balik sa Panimalay',
    quizCompletedTitle: 'Nahuman na ang Pagsusulit!',
    xpGainedLabel: 'XP nga Nadawat',
    newBadgesUnlocked: 'Bag-ong mga Badge nga Naablihan',
    correctAnswersCount: 'Hustong mga Tubag',
    bestStreakLabel: 'Labing Taas nga Sunod-sunod',
    selectAnswerPrompt: 'Pilia ang hustong tubag sa Bibliya sa ubos',
    trueFalsePrompt: 'Tinoa kung Tinuod (True) o Bakak (False) kini nga pahayag sa Bibliya',
    fillBlankPrompt: 'Ibutang ang kulang nga pulong aron makompleto ang bersikulo',
    shortAnswerPrompt: 'I-type ang hustong tawo, dapit, basahon, o tubag sa Bibliya',
    matchingPrompt: 'Pindota ang sa wala ug ipares sa hustong tubag sa tuo',
    openEndedPrompt: 'Isulat ang imong pamalandong sa Bibliya sa ubos',
    trueLabel: 'TINUOD (TRUE)',
    falseLabel: 'BAKAK (FALSE)',
    typeAnswerPlaceholder: 'I-type ang imong tubag...',
    submitAnswerBtn: 'Isumite ang Tubag',
    wordBankMode: 'Pagpili og Pulong',
    typeWordMode: 'I-type ang Tubag',
    matchingColumnA: 'Butang sa Bibliya',
    matchingColumnB: 'Kapares ngaTubag',
    submitMatchesBtn: 'Isumite ang mga Pares',
    clearMatchesBtn: 'I-reset',
    pointsEarned: 'Puntos nga Nadawat',
    speedBonus: 'Bonus sa Katulin',
    streakBonus: 'Bonus sa Sunod-sunod',
    listenQuestionBtn: 'Paminawa',
    tooltipScore:
      'Imong puntos alang niining duwaa lamang. Kada saktong tubag makakuha ug {correctBase} puntos. Paspas nga pagtubag makakuha hangtod sa {speedBonusMax} bonus puntos. Ang sunod-sunod nga sakto nagdugang ug {streakStep} bonus puntos kada streak, hangtod sa {streakMax}. Ang sayop o naabtan sa oras kay {wrongPoints} puntos, ug walay ikuha.',
    tooltipAccuracy:
      'Pila ka pangutana ang imong nasakto sa tanang pangutana sa duwa. Ang giawasan ug naabtan sa oras giisip nga sayop. Pananglitan: 2 sa 5 sakto = 40%.',
    tooltipBestStreak:
      'Imong pinakataas nga sunod-sunod nga saktong tubag niining duwaa. Ang sayop nga tubag o naabtan sa oras mag-reset sa streak.',
    tooltipXpGained:
      'Ang XP maoy imong dugay nga pag-uswag. Makakuha kag mga 1 XP kada {scoreDivisor} puntos sa Score, labing minos {minPerCorrect} XP kada saktong tubag, plus {perfectBonus} bonus XP alang sa perpektong duwa. Ang XP mopasaka sa imong lebel ug ranggo sa leaderboard.',
    tooltipLevelProgress:
      'Mosaka ang lebel samtang makakuha kag XP. Kada bag-ong lebel nagkinahanglan ug dugang XP kaysa sa una.',
    tooltipLifetimeAccuracy:
      'Ipakita human ka makaduwa ug {minQuestions} ka pangutana.',
    ariaHowScoreCalculated: 'Giunsa pagkwenta ang Score',
    ariaHowAccuracyCalculated: 'Giunsa pagkwenta ang Accuracy',
    ariaHowBestStreakCalculated: 'Giunsa pagkwenta ang Best Streak',
    ariaHowXpCalculated: 'Giunsa pagkwenta ang XP',
    ariaHowLevelCalculated: 'Giunsa pagkwenta ang Level',
    ariaHowLifetimeAccuracyCalculated: 'Giunsa pagkwenta ang Lifetime Accuracy',
  },
  ko: {
    tagline: '성경 지식. 글로벌 대결.',
    navHome: '홈',
    navPlay: '퀴즈 시작',
    navMultiplayer: '멀티플레이',
    navLive: 'LIVE',
    navRankings: '랭킹',
    navProfile: '프로필',
    navAdmin: '관리자',
    heroHeadline: '글로벌 매치와 실시간 대회로 성경 지식을 마스터하세요',
    heroSubtitle:
      '무제한 성경 퀴즈, 매 게임 새로운 도전, 실시간 글로벌 매칭, 교회 토너먼트 아레나, 대형 강당 LIVE 이벤트를 경험하세요.',
    ctaStartQuiz: '솔로 퀴즈 플레이',
    ctaHostLive: 'LIVE 이벤트 주최',
    ctaMultiplayer: '글로벌 매칭',
    dailyChallenge: '일일 성경 챌린지',
    dailySubtitle: '매일 새로운 성경 문제 · 2배 XP 및 연속 기록 보호',
    playDailyNow: '일일 챌린지 시작',
    specialModesTitle: '특별 성경 퀴즈 모드',
    categoriesTitle: '구약 및 신약 성경 카테고리',
    soloConfigTitle: '성경 퀴즈 세션 설정',
    soloConfigSubtitle:
      '게임 모드, 퀴즈 유형, 성경 카테고리, 난이도 및 한국어 음성 내레이션을 선택하세요.',
    selectGameMode: '퀴즈 목적 / 게임 모드',
    modePractice: '연습 모드 (Practice)',
    modePracticeDesc: '편안한 학습 · 타이머 선택 가능 · 랭킹 영향 없음',
    modeChallenge: '챌린지 모드 (Challenge)',
    modeChallengeDesc: '점수 획득 · XP 보상 · 개인 최고 기록 달성',
    modeRanked: '랭크 모드 (Ranked)',
    modeRankedDesc: '엄격한 규칙 · 표준 타이머 · 글로벌 리더보드 반영',
    selectCategory: '성경 카테고리 선택',
    selectDifficulty: '난이도 선택',
    selectFormat: '퀴즈 유형 (Quiz Type)',
    questionCount: '문제 수',
    enableTimer: '카운트다운 타이머',
    enableNarration: '자동 음성 내레이션 (TTS)',
    narrateInstructionsLabel: '퀴즈 안내 설명 읽기',
    narrateChoicesLabel: '선택지 함께 읽기 (접근성 설정)',
    freshPoolLabel: '새로운 문제 우선 출제',
    freshPoolDesc: '최근 풀지 않은 성경 문제 우선',
    generateAndPlay: '성경 퀴즈 시작',
    generatingAiQuestions: '성경 정경 퀴즈 문제를 준비하고 있습니다...',
    scriptureReference: '성경 구절',
    explanation: '성경적 배경 및 해설',
    nextQuestion: '다음 문제',
    finishQuiz: '최종 결과 보기',
    scoreLabel: '점수',
    streakLabel: '연속 정답',
    accuracyLabel: '정확도',
    multiplayerLobby: '실시간 글로벌 멀티플레이 아레나',
    quickMatch: '빠른 글로벌 매치',
    createPrivateRoom: '비공개 방 만들기',
    joinByPin: 'PIN 번호로 입장',
    liveEventCenter: '교회 및 수련회 LIVE 이벤트 모드',
    createLiveEvent: 'LIVE 이벤트 생성',
    openProjectorView: '16:9 프로젝터 화면 열기',
    joinLiveEvent: '참가자 모바일 입장',
    timerSwitchLabel: '서버 공식 타이머',
    globalLeaderboards: '글로벌 명예의 전당',
    signInWithGoogle: 'Google 계정으로 로그인',
    signOut: '로그아웃',
    saveProfile: '프로필 및 설정 저장',
    questionPrefix: '문제',
    optionsPrefix: '보기',
    correctFeedback: '정답입니다!',
    incorrectFeedback: '오답입니다.',
    timeoutFeedback: '시간 초과!',
    playAgain: '다시 플레이',
    backToHome: '홈으로 돌아가기',
    quizCompletedTitle: '퀴즈 세션 완료!',
    xpGainedLabel: '획득한 XP',
    newBadgesUnlocked: '새로 획득한 배지',
    correctAnswersCount: '정답 수',
    bestStreakLabel: '최고 연속 정답',
    selectAnswerPrompt: '아래에서 올바른 성경 정답을 선택하세요',
    trueFalsePrompt: '다음 성경 내용이 참(True)인지 거짓(False)인지 선택하세요',
    fillBlankPrompt: '빈칸에 들어갈 알맞은 성경 단어를 선택하거나 입력하세요',
    shortAnswerPrompt: '올바른 성경 인물, 장소, 성경책 또는 단답형 정답을 입력하세요',
    matchingPrompt: '왼쪽 항목을 탭한 후 오른쪽에서 알맞은 짝을 탭하세요',
    openEndedPrompt: '성경 묵상 내용을 작성하고 성경 해설과 비교해 보세요',
    trueLabel: '참 (TRUE)',
    falseLabel: '거짓 (FALSE)',
    typeAnswerPlaceholder: '정답을 입력하세요...',
    submitAnswerBtn: '정답 제출',
    wordBankMode: '단어 선택',
    typeWordMode: '직접 입력',
    matchingColumnA: '성경 항목',
    matchingColumnB: '관련 내용',
    submitMatchesBtn: '매칭 제출하기',
    clearMatchesBtn: '초기화',
    pointsEarned: '획득 점수',
    speedBonus: '스피드 보너스',
    streakBonus: '연속 정답 보너스',
    listenQuestionBtn: '문제 듣기',
    tooltipScore:
      '이 게임의 점수입니다. 정답당 {correctBase}점을 획득합니다. 빠른 답변 시 최대 {speedBonusMax}점의 스피드 보너스가 부여됩니다. 연속 정답 시 연승당 {streakStep}점(최대 {streakMax}점)이 추가됩니다. 오답, 건너뛰기, 시간 초과는 {wrongPoints}점이며 감점은 없습니다.',
    tooltipAccuracy:
      '이 게임의 전체 질문 중 맞힌 질문의 수입니다. 건너뛴 질문과 시간 초과는 오답으로 처리됩니다. 예: 5문제 중 2문제 정답 = 40%.',
    tooltipBestStreak:
      '이 게임 중 가장 긴 연속 정답 기록입니다. 오답, 건너뛰기 또는 시간 초과 시 연승이 초기화됩니다.',
    tooltipXpGained:
      'XP는 장기적인 성장 기록입니다. 점수 {scoreDivisor}점당 약 1 XP, 정답당 최소 {minPerCorrect} XP를 얻으며, 만점 시 {perfectBonus} XP 보너스가 지급됩니다. XP는 레벨과 리더보드 순위를 올립니다.',
    tooltipLevelProgress:
      'XP를 획득함에 따라 레벨이 올라갑니다. 다음 레벨로 올라가려면 더 많은 XP가 필요합니다.',
    tooltipLifetimeAccuracy:
      '{minQuestions}문제를 플레이한 후 표시됩니다.',
    ariaHowScoreCalculated: 'Score 계산 방법',
    ariaHowAccuracyCalculated: 'Accuracy 계산 방법',
    ariaHowBestStreakCalculated: 'Best Streak 계산 방법',
    ariaHowXpCalculated: 'XP 계산 방법',
    ariaHowLevelCalculated: 'Level 계산 방법',
    ariaHowLifetimeAccuracyCalculated: 'Lifetime Accuracy 계산 방법',
  },
  'zh-CN': {
    tagline: '圣经知识 · 全球竞技',
    navHome: '首页',
    navPlay: '开始答题',
    navMultiplayer: '多人对战',
    navLive: '现场 LIVE',
    navRankings: '排行榜',
    navProfile: '个人资料',
    navAdmin: '管理后台',
    heroHeadline: '通过全球实时竞技与现场互动精通圣经真理',
    heroSubtitle:
      '海量圣经题库、每局全新挑战、实时全球匹配、教会锦标赛竞技场以及礼堂级 LIVE 现场活动。',
    ctaStartQuiz: '单人圣经测验',
    ctaHostLive: '主持 LIVE 活动',
    ctaMultiplayer: '全球实时对战',
    dailyChallenge: '每日圣经挑战',
    dailySubtitle: '每日精选圣经题目 · 赢取双倍 XP 与连胜护盾',
    playDailyNow: '开始每日挑战',
    specialModesTitle: '特色圣经闯关模式',
    categoriesTitle: '旧约与新约圣经分类',
    soloConfigTitle: '配置您的圣经测验',
    soloConfigSubtitle:
      '选择游戏模式、测验题型、圣经分类、难度级别以及中文语音朗读设置。',
    selectGameMode: '测验目的 / 游戏模式',
    modePractice: '练习模式 (Practice)',
    modePracticeDesc: '轻松学习 · 可选计时器 · 不影响排名',
    modeChallenge: '挑战模式 (Challenge)',
    modeChallengeDesc: '计分挑战 · XP 经验奖励 · 个人最佳记录',
    modeRanked: '排位模式 (Ranked)',
    modeRankedDesc: '严格规则 · 标准计时 · 计入全球排行榜',
    selectCategory: '选择圣经分类',
    selectDifficulty: '选择难度级别',
    selectFormat: '测验题型 (Quiz Type)',
    questionCount: '题目数量',
    enableTimer: '倒计时器',
    enableNarration: '自动语音朗读 (TTS)',
    narrateInstructionsLabel: '朗读答题说明',
    narrateChoicesLabel: '朗读答案选项（无障碍辅助）',
    freshPoolLabel: '优先未做新题',
    freshPoolDesc: '避免重复近期题目',
    generateAndPlay: '开始圣经测验',
    generatingAiQuestions: '正在准备圣经正典题目...',
    scriptureReference: '经文出处',
    explanation: '圣经背景与解析',
    nextQuestion: '下一题',
    finishQuiz: '查看最终成绩',
    scoreLabel: '得分',
    streakLabel: '连胜',
    accuracyLabel: '准确率',
    multiplayerLobby: '全球实时多人竞技场',
    quickMatch: '快速全球匹配',
    createPrivateRoom: '创建私人房间',
    joinByPin: '输入 PIN 码加入',
    liveEventCenter: '教会与礼堂 LIVE 现场互动模式',
    createLiveEvent: '创建 LIVE 现场活动',
    openProjectorView: '打开 16:9 投影大屏',
    joinLiveEvent: '手机端参赛入口',
    timerSwitchLabel: '服务器权威计时器',
    globalLeaderboards: '全球冠军荣耀榜',
    signInWithGoogle: '使用 Google 账号登录',
    signOut: '退出登录',
    saveProfile: '保存个人资料与设置',
    questionPrefix: '题目',
    optionsPrefix: '选项',
    correctFeedback: '回答正确！',
    incorrectFeedback: '回答错误。',
    timeoutFeedback: '时间到！',
    playAgain: '再玩一次',
    backToHome: '返回首页',
    quizCompletedTitle: '圣经测验完成！',
    xpGainedLabel: '获得 XP',
    newBadgesUnlocked: '解锁新徽章',
    correctAnswersCount: '正确题数',
    bestStreakLabel: '最高连胜',
    selectAnswerPrompt: '请在下方选择正确的圣经答案',
    trueFalsePrompt: '请判断以下圣经陈述是正确 (True) 还是错误 (False)',
    fillBlankPrompt: '请选择或输入缺失的圣经词语以补全经文',
    shortAnswerPrompt: '请输入正确的圣经人物、地点、书卷或简答答案',
    matchingPrompt: '请先点击左侧项目，再点击右侧对应的圣经配对项',
    openEndedPrompt: '请在下方输入您的圣经思考，并与经文解析对照',
    trueLabel: '正确 (TRUE)',
    falseLabel: '错误 (FALSE)',
    typeAnswerPlaceholder: '请输入您的答案...',
    submitAnswerBtn: '提交答案',
    wordBankMode: '选词填空',
    typeWordMode: '手动输入',
    matchingColumnA: '圣经项目',
    matchingColumnB: '对应内容',
    submitMatchesBtn: '提交全部配对',
    clearMatchesBtn: '重置配对',
    pointsEarned: '获得积分',
    speedBonus: '速度奖励',
    streakBonus: '连胜奖励',
    listenQuestionBtn: '朗读题目',
    tooltipScore:
      '仅限本局游戏的得分。每答对一题得 {correctBase} 分。回答越快可额外获得最高 {speedBonusMax} 分速度加成。连续答对每题加 {streakStep} 分连胜加成（最高 {streakMax} 分）。答错、跳过或超时得 {wrongPoints} 分，不会扣分。',
    tooltipAccuracy:
      '本局游戏中答对题目占总题数的比例。跳过和超时的题目计为答错。例如：5 题答对 2 题 = 40%。',
    tooltipBestStreak:
      '本局游戏中最高连续答对次数。答错、跳过或超时将重置连胜数。',
    tooltipXpGained:
      'XP 是您的长期成长记录。每 {scoreDivisor} 分 Score 约转化 1 XP，每答对一题至少获得 {minPerCorrect} XP，全对完美局额外加 {perfectBonus} XP。XP 可提升您的等级和排行榜名次。',
    tooltipLevelProgress:
      '随着获得 XP，等级将会提升。每个新等级所需的 XP 都高于上一级。',
    tooltipLifetimeAccuracy:
      '在您完成 {minQuestions} 道题后显示。',
    ariaHowScoreCalculated: '如何计算 Score 得分',
    ariaHowAccuracyCalculated: '如何计算 Accuracy 准确率',
    ariaHowBestStreakCalculated: '如何计算 Best Streak 最佳连胜',
    ariaHowXpCalculated: '如何计算 XP 经验',
    ariaHowLevelCalculated: '如何计算 Level 等级',
    ariaHowLifetimeAccuracyCalculated: '如何计算 Lifetime Accuracy 累计准确率',
  },
  'zh-TW': {
    tagline: '聖經知識 · 全球競技',
    navHome: '首頁',
    navPlay: '開始答題',
    navMultiplayer: '多人對戰',
    navLive: '現場 LIVE',
    navRankings: '排行榜',
    navProfile: '個人資料',
    navAdmin: '管理後台',
    heroHeadline: '透過全球即時競技與現場互動精通聖經真理',
    heroSubtitle:
      '海量聖經題庫、每局全新挑戰、即時全球配對、教會錦標賽競技場以及禮堂級 LIVE 現場活動。',
    ctaStartQuiz: '單人聖經測驗',
    ctaHostLive: '主持 LIVE 活動',
    ctaMultiplayer: '全球即時對戰',
    dailyChallenge: '每日聖經挑戰',
    dailySubtitle: '每日精選聖經題目 · 贏取雙倍 XP 與連勝護盾',
    playDailyNow: '開始每日挑戰',
    specialModesTitle: '特色聖經闖關模式',
    categoriesTitle: '舊約與新約聖經分類',
    soloConfigTitle: '配置您的聖經測驗',
    soloConfigSubtitle:
      '選擇遊戲模式、測驗題型、聖經分類、難度級別以及中文語音朗讀設定。',
    selectGameMode: '測驗目的 / 遊戲模式',
    modePractice: '練習模式 (Practice)',
    modePracticeDesc: '輕鬆學習 · 可選計時器 · 不影響排名',
    modeChallenge: '挑戰模式 (Challenge)',
    modeChallengeDesc: '計分挑戰 · XP 經驗獎勵 · 個人最佳記錄',
    modeRanked: '排位模式 (Ranked)',
    modeRankedDesc: '嚴格規則 · 標準計時 · 計入全球排行榜',
    selectCategory: '選擇聖經分類',
    selectDifficulty: '選擇難度級別',
    selectFormat: '測驗題型 (Quiz Type)',
    questionCount: '題目數量',
    enableTimer: '倒計時器',
    enableNarration: '自動語音朗讀 (TTS)',
    narrateInstructionsLabel: '朗讀答題說明',
    narrateChoicesLabel: '朗讀答案選項（無障礙輔助）',
    freshPoolLabel: '優先未做新題',
    freshPoolDesc: '避免重複近期題目',
    generateAndPlay: '開始聖經測驗',
    generatingAiQuestions: '正在準備聖經正典題目...',
    scriptureReference: '經文出處',
    explanation: '聖經背景與解析',
    nextQuestion: '下一題',
    finishQuiz: '查看最終成績',
    scoreLabel: '得分',
    streakLabel: '連勝',
    accuracyLabel: '準確率',
    multiplayerLobby: '全球即時多人競技場',
    quickMatch: '快速全球配對',
    createPrivateRoom: '建立私人房間',
    joinByPin: '輸入 PIN 碼加入',
    liveEventCenter: '教會與禮堂 LIVE 現場互動模式',
    createLiveEvent: '建立 LIVE 現場活動',
    openProjectorView: '打開 16:9 投影大屏',
    joinLiveEvent: '手機端參賽入口',
    timerSwitchLabel: '伺服器權威計時器',
    globalLeaderboards: '全球冠軍榮耀榜',
    signInWithGoogle: '使用 Google 帳號登入',
    signOut: '登出',
    saveProfile: '儲存個人資料與設定',
    questionPrefix: '題目',
    optionsPrefix: '選項',
    correctFeedback: '回答正確！',
    incorrectFeedback: '回答錯誤。',
    timeoutFeedback: '時間到！',
    playAgain: '再玩一次',
    backToHome: '返回首頁',
    quizCompletedTitle: '聖經測驗完成！',
    xpGainedLabel: '獲得 XP',
    newBadgesUnlocked: '解鎖新徽章',
    correctAnswersCount: '正確題數',
    bestStreakLabel: '最高連勝',
    selectAnswerPrompt: '請在下方選擇正確的聖經答案',
    trueFalsePrompt: '請判斷以下聖經陳述是正確 (True) 還是錯誤 (False)',
    fillBlankPrompt: '請選擇或輸入缺失的聖經詞語以補全經文',
    shortAnswerPrompt: '請輸入正確的聖經人物、地點、書卷或簡答答案',
    matchingPrompt: '請先點擊左側項目，再點擊右側對應的聖經配對項',
    openEndedPrompt: '請在下方輸入您的聖經思考，並與經文解析對照',
    trueLabel: '正確 (TRUE)',
    falseLabel: '錯誤 (FALSE)',
    typeAnswerPlaceholder: '請輸入您的答案...',
    submitAnswerBtn: '提交答案',
    wordBankMode: '選詞填空',
    typeWordMode: '手動輸入',
    matchingColumnA: '聖經項目',
    matchingColumnB: '對應內容',
    submitMatchesBtn: '提交全部配對',
    clearMatchesBtn: '重置配對',
    pointsEarned: '獲得積分',
    speedBonus: '速度獎勵',
    streakBonus: '連勝獎勵',
    listenQuestionBtn: '朗讀題目',
    tooltipScore:
      '僅限本局遊戲的得分。每答對一題得 {correctBase} 分。回答越快可額外獲得最高 {speedBonusMax} 分速度加成。連續答對每題加 {streakStep} 分連勝加成（最高 {streakMax} 分）。答錯、跳過或超時得 {wrongPoints} 分，不會扣分。',
    tooltipAccuracy:
      '本局遊戲中答對題目占總題數的比例。跳過和超時的題目計為答錯。例如：5 題答對 2 題 = 40%。',
    tooltipBestStreak:
      '本局遊戲中最高連續答對次數。答錯、跳過或超時將重置連勝數。',
    tooltipXpGained:
      'XP 是您的長期成長記錄。每 {scoreDivisor} 分 Score 約轉化 1 XP，每答對一題至少獲得 {minPerCorrect} XP，全對完美局額外加 {perfectBonus} XP。XP 可提升您的等級和排行榜名次。',
    tooltipLevelProgress:
      '隨着獲得 XP，等級將會提升。每個新等級所需的 XP 都高於上一級。',
    tooltipLifetimeAccuracy:
      '在您完成 {minQuestions} 道題後顯示。',
    ariaHowScoreCalculated: '如何計算 Score 得分',
    ariaHowAccuracyCalculated: '如何計算 Accuracy 準確率',
    ariaHowBestStreakCalculated: '如何計算 Best Streak 最佳連勝',
    ariaHowXpCalculated: '如何計算 XP 經驗',
    ariaHowLevelCalculated: '如何計算 Level 等級',
    ariaHowLifetimeAccuracyCalculated: '如何計算 Lifetime Accuracy 累計準確率',
  },
  es: {
    tagline: 'Conocimiento Bíblico. Competencia Global.',
    navHome: 'Inicio',
    navPlay: 'Jugar',
    navMultiplayer: 'Multijugador',
    navLive: 'EN VIVO',
    navRankings: 'Clasificación',
    navProfile: 'Perfil',
    navAdmin: 'Admin',
    heroHeadline: 'Domina las Escrituras mediante el Juego Global y en Vivo',
    heroSubtitle:
      'Preguntas bíblicas ilimitadas, desafíos nuevos en cada partida, emparejamiento global en tiempo real y eventos EN VIVO para iglesias.',
    ctaStartQuiz: 'Jugar Quiz Individual',
    ctaHostLive: 'Crear Evento EN VIVO',
    ctaMultiplayer: 'Partida Global',
    dailyChallenge: 'Desafío Bíblico Diario',
    dailySubtitle: 'Nuevos desafíos cada día · Gana 2x XP',
    playDailyNow: 'Iniciar Desafío Diario',
    specialModesTitle: 'Modos Especiales de las Escrituras',
    categoriesTitle: 'Categorías del Antiguo y Nuevo Testamento',
    soloConfigTitle: 'Configura tu Sesión Bíblica',
    soloConfigSubtitle:
      'Elige el modo de juego, tipo de cuestionario, categoría bíblica, dificultad y narración de voz.',
    selectGameMode: 'Propósito / Modo de Juego',
    modePractice: 'Modo Práctica',
    modePracticeDesc: 'Estudio relajado · Temporizador opcional · Sin impacto en rango',
    modeChallenge: 'Modo Desafío',
    modeChallengeDesc: 'Con puntuación · Recompensas XP · Récords personales',
    modeRanked: 'Modo Clasificatorio',
    modeRankedDesc: 'Reglas estrictas · Impacto en la clasificación global',
    selectCategory: 'Seleccionar Categoría',
    selectDifficulty: 'Nivel de Dificultad',
    selectFormat: 'Tipo de Quiz (Quiz Type)',
    questionCount: 'Cantidad de Preguntas',
    enableTimer: 'Temporizador',
    enableNarration: 'Narración Automática (TTS)',
    narrateInstructionsLabel: 'Narrar Instrucciones',
    narrateChoicesLabel: 'Narrar Opciones de Respuesta (Accesibilidad)',
    freshPoolLabel: 'Ampliar Banco de Preguntas',
    freshPoolDesc: 'Priorizar preguntas no vistas recientemente',
    generateAndPlay: 'Iniciar Quiz Bíblico',
    generatingAiQuestions: 'Preparando preguntas bíblicas canónicas...',
    scriptureReference: 'Referencia Bíblica',
    explanation: 'Contexto y Explicación Bíblica',
    nextQuestion: 'Siguiente Pregunta',
    finishQuiz: 'Ver Resultados Finales',
    scoreLabel: 'Puntos',
    streakLabel: 'Racha',
    accuracyLabel: 'Precisión',
    multiplayerLobby: 'Arena Multijugador Global en Tiempo Real',
    quickMatch: 'Partida Rápida',
    createPrivateRoom: 'Crear Sala Privada',
    joinByPin: 'Unirse con PIN',
    liveEventCenter: 'Modo de Eventos EN VIVO para Auditorios e Iglesias',
    createLiveEvent: 'Crear Evento EN VIVO',
    openProjectorView: 'Abrir Pantalla de Proyector 16:9',
    joinLiveEvent: 'Unirse como Participante',
    timerSwitchLabel: 'Temporizador del Servidor',
    globalLeaderboards: 'Clasificación Mundial de Campeones',
    signInWithGoogle: 'Iniciar sesión con Google',
    signOut: 'Cerrar sesión',
    saveProfile: 'Guardar Perfil y Preferencias',
    questionPrefix: 'Pregunta',
    optionsPrefix: 'Opciones',
    correctFeedback: '¡Correcto!',
    incorrectFeedback: '¡Incorrecto!',
    timeoutFeedback: '¡Tiempo Agotado!',
    playAgain: 'Jugar de nuevo',
    backToHome: 'Volver al Inicio',
    quizCompletedTitle: '¡Sesión de Quiz Completada!',
    xpGainedLabel: 'XP Ganado',
    newBadgesUnlocked: 'Nuevas Medallas Desbloqueadas',
    correctAnswersCount: 'Respuestas Correctas',
    bestStreakLabel: 'Mejor Racha',
    selectAnswerPrompt: 'Seleccione la respuesta bíblica correcta abajo',
    trueFalsePrompt: 'Determine si esta afirmación bíblica es Verdadera o Falsa',
    fillBlankPrompt: 'Complete la palabra bíblica que falta en el pasaje',
    shortAnswerPrompt: 'Escriba la persona, lugar, libro o respuesta bíblica correcta',
    matchingPrompt: 'Toque un elemento a la izquierda y luego su par bíblico a la derecha',
    openEndedPrompt: 'Escriba su reflexión bíblica abajo',
    trueLabel: 'VERDADERO',
    falseLabel: 'FALSO',
    typeAnswerPlaceholder: 'Escribe tu respuesta...',
    submitAnswerBtn: 'Enviar Respuesta',
    wordBankMode: 'Selección de Palabra',
    typeWordMode: 'Escribir Respuesta',
    matchingColumnA: 'Elemento Bíblico',
    matchingColumnB: 'Conexión Correspondiente',
    submitMatchesBtn: 'Enviar Pares',
    clearMatchesBtn: 'Reiniciar',
    pointsEarned: 'Puntos Ganados',
    speedBonus: 'Bono de Velocidad',
    streakBonus: 'Bono de Racha',
    listenQuestionBtn: 'Escuchar',
    tooltipScore:
      'Tus puntos solo para este juego. Cada respuesta correcta gana {correctBase} puntos. Responde más rápido para obtener hasta {speedBonusMax} puntos de bonificación. Acertar respuestas seguidas añade {streakStep} puntos de bonificación por racha, hasta {streakMax}. Las respuestas incorrectas, omitidas o agotadas valen {wrongPoints}, y nunca se resta nada.',
    tooltipAccuracy:
      'Cuántas preguntas respondiste correctamente del total en este juego. Las preguntas omitidas o agotadas cuentan como incorrectas. Ejemplo: 2 de 5 correctas = 40%.',
    tooltipBestStreak:
      'Tu racha más larga de respuestas correctas consecutivas en este juego. Una respuesta incorrecta, omitida o agotada reinicia la racha.',
    tooltipXpGained:
      'El XP es tu progreso a largo plazo. Ganas aprox. 1 XP por cada {scoreDivisor} puntos de Score, al menos {minPerCorrect} XP por respuesta correcta, más {perfectBonus} XP de bonificación por juego perfecto. El XP aumenta tu nivel y tu rango en la tabla de clasificación.',
    tooltipLevelProgress:
      'El nivel sube a medida que ganas XP. Cada nuevo nivel requiere más XP que el anterior.',
    tooltipLifetimeAccuracy:
      'Se muestra después de haber jugado {minQuestions} preguntas.',
    ariaHowScoreCalculated: 'Cómo se calcula el Score',
    ariaHowAccuracyCalculated: 'Cómo se calcula la Accuracy',
    ariaHowBestStreakCalculated: 'Cómo se calcula la Best Streak',
    ariaHowXpCalculated: 'Cómo se calcula el XP',
    ariaHowLevelCalculated: 'Cómo se calcula el Nivel',
    ariaHowLifetimeAccuracyCalculated: 'Cómo se calcula la Accuracy total',
  },
};

export const QUIZ_TYPE_LABELS: Record<
  string,
  Record<SupportedLanguage, string>
> = {
  'Automatic / Mixed': {
    en: 'Automatic / Mixed (Smart Variety)',
    tl: 'Awtomatiko / Halo-halo (Smart Variety)',
    ceb: 'Awtomatiko / Sagol (Smart Variety)',
    ko: '자동 / 혼합 (스마트 다양성)',
    'zh-CN': '自动 / 混合题型 (智能轮换)',
    'zh-TW': '自動 / 混合題型 (智慧輪換)',
    es: 'Automático / Mixto (Variedad Inteligente)',
  },
  'Mixed Formats': {
    en: 'Automatic / Mixed (Smart Variety)',
    tl: 'Awtomatiko / Halo-halo (Smart Variety)',
    ceb: 'Awtomatiko / Sagol (Smart Variety)',
    ko: '자동 / 혼합 (스마트 다양성)',
    'zh-CN': '自动 / 混合题型 (智能轮换)',
    'zh-TW': '自動 / 混合題型 (智慧輪換)',
    es: 'Automático / Mixto (Variedad Inteligente)',
  },
  'Multiple Choice': {
    en: 'Multiple Choice (3–5 Options)',
    tl: 'Multiple Choice (3–5 Pagpipilian)',
    ceb: 'Multiple Choice (3–5 Kapilian)',
    ko: '객관식 선택형 (3~5개 보기)',
    'zh-CN': '多项选择题 (3–5 个选项)',
    'zh-TW': '多項選擇題 (3–5 個選項)',
    es: 'Opción Múltiple (3–5 Opciones)',
  },
  'True or False': {
    en: 'True or False',
    tl: 'Tama o Mali (True or False)',
    ceb: 'Tinuod o Bakak (True or False)',
    ko: '참 / 거짓 (True or False)',
    'zh-CN': '是非判断题 (True or False)',
    'zh-TW': '是非判斷題 (True or False)',
    es: 'Verdadero o Falso',
  },
  'Fill in the Blank': {
    en: 'Fill in the Blank',
    tl: 'Punan ang Patlang (Fill in the Blank)',
    ceb: 'Pun-i ang Blangko (Fill in the Blank)',
    ko: '빈칸 채우기 (Fill in the Blank)',
    'zh-CN': '经文填空题 (Fill in the Blank)',
    'zh-TW': '經文填空題 (Fill in the Blank)',
    es: 'Completar el Espacio en Blanco',
  },
  Matching: {
    en: 'Matching Pairs',
    tl: 'Pagtutugma (Matching)',
    ceb: 'Pagpares (Matching)',
    ko: '짝맞추기 (Matching)',
    'zh-CN': '圣经配对题 (Matching)',
    'zh-TW': '聖經配對題 (Matching)',
    es: 'Emparejamiento (Matching)',
  },
  'Short Answer': {
    en: 'Short Answer',
    tl: 'Maikling Sagot (Short Answer)',
    ceb: 'Mubo nga Tubag (Short Answer)',
    ko: '단답형 (Short Answer)',
    'zh-CN': '简答题 (Short Answer)',
    'zh-TW': '簡答題 (Short Answer)',
    es: 'Respuesta Corta',
  },
  'Mixed Challenge': {
    en: 'Mixed Challenge',
    tl: 'Pinaghalong Hamon (Mixed Challenge)',
    ceb: 'Sagol nga Hagit (Mixed Challenge)',
    ko: '복합 챌린지 (Mixed Challenge)',
    'zh-CN': '综合挑战模式 (Mixed Challenge)',
    'zh-TW': '綜合挑戰模式 (Mixed Challenge)',
    es: 'Desafío Mixto',
  },
  'Open-Ended Reflection': {
    en: 'Open-Ended Reflection (Practice Only)',
    tl: 'Bukas na Pagninilay (Practice Lamang)',
    ceb: 'Bukas nga Pamalandong (Practice Lamang)',
    ko: '주관식 성경 묵상 (연습 전용)',
    'zh-CN': '开放式圣经思考 (仅限练习)',
    'zh-TW': '開放式聖經思考 (僅限練習)',
    es: 'Reflexión Abierta (Solo Práctica)',
  },
};

export const DIFFICULTY_LABELS: Record<
  string,
  Record<SupportedLanguage, string>
> = {
  'Mixed Difficulty': {
    en: 'Mixed Difficulty',
    tl: 'Halo-halong Antas',
    ceb: 'Sagol nga Kalisod',
    ko: '혼합 난이도',
    'zh-CN': '混合难度',
    'zh-TW': '混合難度',
    es: 'Dificultad Mixta',
  },
  Beginner: {
    en: 'Beginner',
    tl: 'Baguhan (Beginner)',
    ceb: 'Sinugdanan (Beginner)',
    ko: '초급 (Beginner)',
    'zh-CN': '入门 (Beginner)',
    'zh-TW': '入門 (Beginner)',
    es: 'Principiante',
  },
  Easy: {
    en: 'Easy',
    tl: 'Madali (Easy)',
    ceb: 'Sayon (Easy)',
    ko: '쉬움 (Easy)',
    'zh-CN': '简单 (Easy)',
    'zh-TW': '簡單 (Easy)',
    es: 'Fácil',
  },
  Intermediate: {
    en: 'Intermediate',
    tl: 'Katamtaman (Intermediate)',
    ceb: 'Kasarangan (Intermediate)',
    ko: '중급 (Intermediate)',
    'zh-CN': '中级 (Intermediate)',
    'zh-TW': '中級 (Intermediate)',
    es: 'Intermedio',
  },
  Advanced: {
    en: 'Advanced',
    tl: 'Masulong (Advanced)',
    ceb: 'Abante (Advanced)',
    ko: '고급 (Advanced)',
    'zh-CN': '高级 (Advanced)',
    'zh-TW': '高級 (Advanced)',
    es: 'Avanzado',
  },
  Expert: {
    en: 'Expert',
    tl: 'Dalubhasa (Expert)',
    ceb: 'Eksperto (Expert)',
    ko: '전문가 (Expert)',
    'zh-CN': '专家 (Expert)',
    'zh-TW': '專家 (Expert)',
    es: 'Experto',
  },
};

export const CATEGORY_LABELS: Record<
  string,
  Partial<Record<SupportedLanguage, string>>
> = {
  'ALL CATEGORIES': {
    en: 'ALL CATEGORIES',
    tl: 'LAHAT NG KATEGORYA',
    ceb: 'TANANG KATEGORYA',
    ko: '모든 성경 카테고리',
    'zh-CN': '全部圣经分类',
    'zh-TW': '全部聖經分類',
    es: 'TODAS LAS CATEGORÍAS',
  },
  Genesis: {
    en: 'Genesis',
    tl: 'Genesis',
    ceb: 'Genesis',
    ko: '창세기 (Genesis)',
    'zh-CN': '创世记 (Genesis)',
    'zh-TW': '創世記 (Genesis)',
    es: 'Génesis',
  },
  Exodus: {
    en: 'Exodus',
    tl: 'Exodo',
    ceb: 'Exodo',
    ko: '출애굽기 (Exodus)',
    'zh-CN': '出埃及记 (Exodus)',
    'zh-TW': '出埃及記 (Exodus)',
    es: 'Éxodo',
  },
  'Psalms and Proverbs': {
    en: 'Psalms and Proverbs',
    tl: 'Mga Awit at Kawikaan',
    ceb: 'Mga Salmo ug Panultihon',
    ko: '시편과 잠언',
    'zh-CN': '诗篇与箴言',
    'zh-TW': '詩篇與箴言',
    es: 'Salmos y Proverbios',
  },
  'The Four Gospels': {
    en: 'The Four Gospels',
    tl: 'Ang Apat na Ebanghelyo',
    ceb: 'Ang Upat ka Ebanghelyo',
    ko: '사복음서 (마태·마가·누가·요한)',
    'zh-CN': '四福音书',
    'zh-TW': '四福音書',
    es: 'Los Cuatro Evangelios',
  },
  Acts: {
    en: 'Acts',
    tl: 'Mga Gawa',
    ceb: 'Mga Buhat',
    ko: '사도행전 (Acts)',
    'zh-CN': '使徒行传 (Acts)',
    'zh-TW': '使徒行傳 (Acts)',
    es: 'Hechos',
  },
  'Biblical Characters': {
    en: 'Biblical Characters',
    tl: 'Mga Tauhan sa Bibliya',
    ceb: 'Mga Karakter sa Bibliya',
    ko: '성경 인물',
    'zh-CN': '圣经人物',
    'zh-TW': '聖經人物',
    es: 'Personajes Bíblicos',
  },
  'Biblical Geography': {
    en: 'Biblical Geography',
    tl: 'Heograpiya ng Bibliya',
    ceb: 'Heograpiya sa Bibliya',
    ko: '성경 지리 및 장소',
    'zh-CN': '圣经地理与地点',
    'zh-TW': '聖經地理與地點',
    es: 'Geografía Bíblica',
  },
  'Biblical History': {
    en: 'Biblical History',
    tl: 'Kasaysayan ng Bibliya',
    ceb: 'Kasaysayan sa Bibliya',
    ko: '성경 역사 및 연대기',
    'zh-CN': '圣经历史与年代',
    'zh-TW': '聖經歷史與年代',
    es: 'Historia Bíblica',
  },
  Miracles: {
    en: 'Miracles',
    tl: 'Mga Himala',
    ceb: 'Mga Milagro',
    ko: '성경의 기적',
    'zh-CN': '圣经神迹',
    'zh-TW': '聖經神蹟',
    es: 'Milagros',
  },
  Parables: {
    en: 'Parables',
    tl: 'Mga Talinghaga',
    ceb: 'Mga Sambingay',
    ko: '예수님의 비유',
    'zh-CN': '耶稣的比喻',
    'zh-TW': '耶穌的比喻',
    es: 'Parábolas',
  },
  Prophets: {
    en: 'Prophets',
    tl: 'Mga Propeta',
    ceb: 'Mga Manalagna',
    ko: '선지자들',
    'zh-CN': '众先知',
    'zh-TW': '眾先知',
    es: 'Profetas',
  },
  Apostles: {
    en: 'Apostles',
    tl: 'Mga Apostol',
    ceb: 'Mga Apostoles',
    ko: '사도들',
    'zh-CN': '众使徒',
    'zh-TW': '眾使徒',
    es: 'Apóstoles',
  },
  'Kings and Queens': {
    en: 'Kings and Queens',
    tl: 'Mga Hari at Reyna',
    ceb: 'Mga Hari ug Rayna',
    ko: '성경의 왕과 왕비',
    'zh-CN': '列王与王后',
    'zh-TW': '列王與王后',
    es: 'Reyes y Reinas',
  },
  'Women of the Bible': {
    en: 'Women of the Bible',
    tl: 'Kababaihan sa Bibliya',
    ceb: 'Kababayen-an sa Bibliya',
    ko: '성경의 여인들',
    'zh-CN': '圣经中的女性',
    'zh-TW': '聖經中的女性',
    es: 'Mujeres de la Biblia',
  },
  Revelation: {
    en: 'Revelation',
    tl: 'Pahayag (Apocalipsis)',
    ceb: 'Gipadayag (Pinadayag)',
    ko: '요한계시록 (Revelation)',
    'zh-CN': '启示录 (Revelation)',
    'zh-TW': '啟示錄 (Revelation)',
    es: 'Apocalipsis',
  },
  'Pauline Epistles': {
    en: 'Pauline Epistles',
    tl: 'Mga Sulat ni Pablo',
    ceb: 'Mga Sulat ni Pablo',
    ko: '바울 서신',
    'zh-CN': '保罗书信',
    'zh-TW': '保羅書信',
    es: 'Epístolas Paulinas',
  },
  'Major Prophets': {
    en: 'Major Prophets',
    tl: 'Mga Pangunahing Propeta',
    ceb: 'Mga Dagkong Manalagna',
    ko: '대선지서',
    'zh-CN': '大先知书',
    'zh-TW': '大先知書',
    es: 'Profetas Mayores',
  },
};

export function getLocalizedCategoryLabel(
  category: string,
  lang: SupportedLanguage
): string {
  return CATEGORY_LABELS[category]?.[lang] || category;
}

export function getLocalizedFormatLabel(
  format: string,
  lang: SupportedLanguage
): string {
  return QUIZ_TYPE_LABELS[format]?.[lang] || format;
}

export function getLocalizedDifficultyLabel(
  difficulty: string,
  lang: SupportedLanguage
): string {
  return DIFFICULTY_LABELS[difficulty]?.[lang] || difficulty;
}
